"use client";

import { ArrowLeft, Shield } from "lucide-react";
import Link from "next/link";

import { PageEyebrow } from "@/components/public/page-eyebrow";
import {
  PUBLIC_CONTAINER,
  PUBLIC_PANEL_RADIUS,
  PUBLIC_SECTION_PY_CONTENT,
} from "@/components/public/public-layout-styles";
import { cn } from "@/lib/utils";

interface PrivacyContentProps {
  lastUpdated: string;
}

export function PrivacyContent({ lastUpdated }: PrivacyContentProps) {
  return (
    <main className="relative z-10">
      <div className={cn(PUBLIC_CONTAINER, "max-w-4xl", PUBLIC_SECTION_PY_CONTENT)}>
        {/* Back Link */}
        <Link
          className="text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-2 text-sm transition-colors"
          href="/"
        >
          <ArrowLeft className="size-4" />
          Voltar ao início
        </Link>

        {/* Page Header */}
        <div className="mb-12">
          <PageEyebrow className="mb-4" icon={Shield}>
            Proteção de Dados
          </PageEyebrow>
          <h1 className="mb-4 text-4xl font-semibold tracking-tight">Política de privacidade</h1>
          <p className="text-muted-foreground">Última atualização: {lastUpdated}</p>
        </div>

        {/* Content Card */}
        <div
          className={cn("border-border/50 bg-card border p-6 sm:p-8 lg:p-10", PUBLIC_PANEL_RADIUS)}
        >
          <div className="prose max-w-none">
            <p>
              A <strong>AGROSISTEMAS SOFTWARES AGRO LTDA</strong>, pessoa jurídica de direito
              privado, inscrita no CNPJ sob o nº 51.145.228/0001-00, com sede na Rua Pelotas, 19,
              sala 01B, Centro, Machadinho - RS, CEP 99880-000, Brasil, denominada simplesmente{" "}
              <strong>"AgroProj"</strong>, "nós", "nosso" ou "Plataforma", está comprometida com a
              proteção da privacidade e dos dados pessoais de nossos usuários. Esta Política de
              Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações
              em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>

            <h2>1. Dados que Coletamos</h2>

            <h3>1.1 Dados fornecidos por você</h3>
            <ul>
              <li>
                <strong>Dados de cadastro:</strong> nome completo, e-mail, telefone, CPF/CNPJ
              </li>
              <li>
                <strong>Dados profissionais:</strong> informações sobre sua empresa ou propriedade
                rural
              </li>
              <li>
                <strong>Dados de produtores:</strong> informações de produtores rurais cadastrados
                na plataforma, incluindo dados de propriedades e projetos agrícolas
              </li>
              <li>
                <strong>Dados financeiros:</strong> informações de projetos de investimento e
                financiamento rural
              </li>
            </ul>

            <h3>1.2 Dados coletados automaticamente</h3>
            <ul>
              <li>Endereço IP e dados de geolocalização aproximada</li>
              <li>Tipo de dispositivo e navegador utilizado</li>
              <li>Páginas acessadas e tempo de permanência</li>
              <li>Cookies e tecnologias similares</li>
            </ul>

            <h2>2. Finalidade do Tratamento</h2>
            <p>Utilizamos seus dados pessoais para:</p>
            <ul>
              <li>Fornecer e manter nossos serviços de gestão agrícola</li>
              <li>Gerar relatórios de projetos de investimento rural</li>
              <li>Processar cadastros de produtores e propriedades</li>
              <li>Enviar comunicações sobre o serviço e atualizações</li>
              <li>Cumprir obrigações legais e regulatórias</li>
              <li>Melhorar e personalizar a experiência do usuário</li>
              <li>Prevenir fraudes e garantir a segurança da plataforma</li>
            </ul>

            <h2>3. Base Legal para o Tratamento</h2>
            <p>O tratamento de dados pessoais é realizado com base em:</p>
            <ul>
              <li>
                <strong>Execução de contrato:</strong> para prestação dos serviços contratados
              </li>
              <li>
                <strong>Consentimento:</strong> quando você nos autoriza expressamente
              </li>
              <li>
                <strong>Legítimo interesse:</strong> para melhorias no serviço e comunicações
                relevantes
              </li>
              <li>
                <strong>Cumprimento de obrigação legal:</strong> quando exigido por lei
              </li>
            </ul>

            <h2>4. Compartilhamento de Dados</h2>
            <p>Podemos compartilhar seus dados com:</p>
            <ul>
              <li>
                <strong>Prestadores de serviço:</strong> empresas que nos auxiliam na operação da
                plataforma (hospedagem, processamento de pagamentos, envio de e-mails)
              </li>
              <li>
                <strong>Instituições financeiras:</strong> quando necessário para análise de
                projetos de financiamento rural
              </li>
              <li>
                <strong>Autoridades públicas:</strong> quando exigido por lei ou ordem judicial
              </li>
            </ul>
            <p>
              <strong>Não vendemos</strong> seus dados pessoais a terceiros.
            </p>

            <h2>5. Armazenamento e Segurança</h2>
            <p>
              Seus dados são armazenados em servidores seguros com criptografia e controles de
              acesso. Implementamos medidas técnicas e organizacionais para proteger seus dados
              contra acesso não autorizado, perda ou destruição.
            </p>
            <p>
              Os dados são retidos pelo período necessário para cumprir as finalidades descritas
              nesta política ou conforme exigido por lei.
            </p>

            <h2>6. Seus Direitos (LGPD)</h2>
            <p>Conforme a LGPD, você tem direito a:</p>
            <ul>
              <li>Confirmar a existência de tratamento de seus dados</li>
              <li>Acessar seus dados pessoais</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
              <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários</li>
              <li>Solicitar a portabilidade dos dados</li>
              <li>Revogar o consentimento a qualquer momento</li>
              <li>Obter informações sobre compartilhamento com entidades públicas e privadas</li>
            </ul>

            <h2>7. Cookies</h2>
            <p>
              Utilizamos cookies para melhorar sua experiência na plataforma. Cookies são pequenos
              arquivos armazenados em seu dispositivo que nos ajudam a:
            </p>
            <ul>
              <li>Manter você conectado à sua conta</li>
              <li>Lembrar suas preferências</li>
              <li>Analisar o uso da plataforma para melhorias</li>
            </ul>
            <p>
              Você pode configurar seu navegador para recusar cookies, mas isso pode afetar a
              funcionalidade do serviço.
            </p>

            <h2>8. Alterações nesta Política</h2>
            <p>
              Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você
              sobre alterações significativas por e-mail ou através de aviso na plataforma.
            </p>

            <h2>9. Contato</h2>
            <p>
              Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato
              conosco:
            </p>
            <ul>
              <li>
                <strong>E-mail:</strong> contato@agroproj.com
              </li>
              <li>
                <strong>Endereço:</strong> Rua Pelotas, 19, sala 01B, Centro, Machadinho - RS, CEP
                99880-000, Brasil
              </li>
            </ul>
          </div>

          {/* Legal Note */}
          <div className={cn("border-border/50 bg-surface mt-10 border p-4", PUBLIC_PANEL_RADIUS)}>
            <p className="text-muted-foreground text-sm">
              Esta Política de Privacidade é regida pelas leis brasileiras, especialmente a Lei
              Geral de Proteção de Dados (Lei nº 13.709/2018).
            </p>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link
            className="text-primary hover:text-primary/80 text-sm transition-colors"
            href="/terms"
          >
            Termos de uso →
          </Link>
        </div>
      </div>
    </main>
  );
}
